/*  
<summary>
  Functional Component: StoreOptionMenuList
  Developer: Ashutosh Modgil
  Created Date: 11-April-2025
  Description: Renders a OptionMenu management interface with features for viewing, adding, editing, deleting, and filtering staff. Includes pagination, popups for detailed views, and manual refresh and invalid staff toggles.
</summary>
<returns>JSX for the OptionMenu Management interface</returns>
*/

import { faFilter, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CellContext } from "@tanstack/react-table";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import noMessageIcon from "../../../assets/Images/svg/018-empty.svg";
import { ActionType } from "../../../constants/enums/action-type-enum";
import { ColumnType } from "../../../constants/enums/grid-column-type-enum";
import { ModuleType } from "../../../constants/enums/module-type-enum";
import userSettingEnum from "../../../constants/enums/user-setting-enum";
import { useStore } from "../../../contexts/store-provider";
import { optionInitialFilterState } from "../../../core/initialState/initial-filter-state";
import { isOperationPermittedRoleBase } from "../../../helpers/action-permission-helper";
import {
  getBusinessTypeIdFromLocalStorage,
  getUserTypeFromLocalStorage,
} from "../../../helpers/localstorage-helper";
import IPageInfo, { IPageLimitInfo } from "../../../models/ICommon";
import { ISorting } from "../../../models/ISorting";
import LoaderButton from "../../../shared-components/button/loader-button";
import FormLoader from "../../../shared-components/FormLoader/FormLoader";
import Grid, { getColumns } from "../../../shared-components/Grid/Grid";
import Pagination from "../../../shared-components/Grid/Pagination";
import LanguageSlider from "../../../shared-components/language-slider/language-slider";
import AddPopupHOC from "../../../shared-components/popup/add/add-popup-hoc";
import DeletePopup from "../../../shared-components/popup/delete/delete-popup";
import DeletePopupHOC from "../../../shared-components/popup/delete/delete-popup-hoc";
import ShowPopupHOC from "../../../shared-components/popup/show/show-popup-hoc";
import { formatMessage } from "../../../translations/format-message";
import AddEditOptionMenu from "./components/add-edit-store-option-menu";
import { invalid } from "moment";
import { set } from "lodash";
import { formatNumberWithCommas } from "../../../constants/constants";
import "./store-option-menu-list.css";

const StoreOptionMenuList: React.FC = (props: any) => {
  const { optionMenuStore, preferencesStore, masterManagementStore } =
    useStore();

  const [openPanel, setOpenPanel] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const {
    inProgress,
    error,
    GetSearchOptionRegistrationFilterService,
    allOptionMenu,
    optionmenuList,
    resetAddUpdateOptionMenuState,
    addUpdateOptionMenuState,
    deleteOptionMenuState,
    DeleteOptionMenuService,
    resetDeleteOptionMenu,
    UpdateStoreOptionOrderIdService,
    resetUpdateStoreOptionOrderState,
    updateStoreOptionOrderState,
    resetOptionStoreFilterDetail,
    getOptionStoreFilter,
    setOptionStoreFilterDetail,
    optionFilterStore,
  } = optionMenuStore;
  const {
    userSetting,
    setUserSetting,
    language,
    islanguageChanged,
    updateLanguageChangedFlag,
  } = preferencesStore;
  const {
    GetAllBusinessTypeService,
    getAllBusinessTypes,
    getAllTimeOptions,
    GetAllTimeService,
  } = masterManagementStore;

  const [optionNameSearch, setOptionNameSearch] = useState(
    optionInitialFilterState.OptionName ?? ""
  );
  const [showInvalidStores, setShowInvalidStores] = useState(
    optionInitialFilterState.IsInvalid ?? false
  );

  const [freeWordSearch, setFreeWordSearch] = useState("");
  const [searchInput, setSearchInput] = useState(freeWordSearch);
  const userType = getUserTypeFromLocalStorage();
  const businessTypeId = getBusinessTypeIdFromLocalStorage();

  const handleInvalidTreatmentFilter = useCallback(() => {
    setShowInvalidStores((prev) => !prev);
    setOptionStoreFilterDetail({
      IsFilterActive: true,
      IsInvalid: showInvalidStores,
      Freeword: searchInput,
    });
    setPageInfo({...pageInfo, currentPage: 1, isRefreshPage: true});
  }, [setOptionStoreFilterDetail, showInvalidStores, searchInput]);

  // function to show free word and also show all data when search field is empty
  const handleSearchClick = () => {
    const searchQuery = searchInput.trim();
    setFreeWordSearch("");
    setSelectedCategory(null);
    setOptionStoreFilterDetail({
      IsFilterActive: true,
      IsInvalid: showInvalidStores,
      Freeword: searchQuery,
    });
    setPageInfo({...pageInfo, currentPage: 1, isRefreshPage: true});
    // if (searchQuery.length === 0) {
    //   // Fetch all data with no filters
    //   GetOptionMenuListService(
    //     pageInfo.currentPage,
    //     pageLimitInfo.pageLimit,
    //     sorting.orderBy,
    //     sorting.column,
    //   );
    //   return;
    // }

    // if (searchQuery.length < 3) {
    //   toast.error(formatMessage("search_word_minimum_length"));
    //   return;
    // }

    setFreeWordSearch(searchQuery);

    // GetSearchOptionRegistrationFilterService(
    //   pageInfo.currentPage,
    //   pageLimitInfo.pageLimit,
    //   searchQuery,
    //   false,
    // );
  };

  const [disablePagination, setDisablePagination] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<number>(0);
  const pageLimitOptions: Array<number> = [10, 50, 100];
  const [pageLimitInfo, setPageLimit] = useState<IPageLimitInfo>({
    pageLimit: userSetting.pageLimitConfig,
    isPageLimitChange: false,
  });

  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    currentPage: 1,
    totalPages: 1,
    isPagerChange: false,
    isRefreshPage: false,
  });

  const [sorting, setSorting] = useState<ISorting>({
    column: "sod_order_id",
    orderBy: "asc",
  });

  const onPageChanged = (pageNumber: number) => {
    setPageInfo({ ...pageInfo, currentPage: pageNumber, isPagerChange: true });
  };

  const setIsRefreshPage = () => {
    setPageInfo({ ...pageInfo, isRefreshPage: false });
  };

  const onPageLimitChanged = (evt: any) => {
    let pages: number;
    setPageInfo({
      ...pageInfo,
      currentPage: 1,
      totalPages: pageInfo.totalPages,
      isPagerChange: false,
    });
    setPageLimit({
      pageLimit: parseInt(evt.target.value),
      isPageLimitChange: true,
    });
    setUserSetting(userSettingEnum.UserLimitConfig, parseInt(evt.target.value));
  };

  // function to handle sorting on grid
  const handleSortingChange = (accessor: string) => {
    setFreeWordSearch("");
    setShowInvalidStores(false);
    // setPageLimit({ ...pageLimitInfo, isPageLimitChange: false });
    // setPageInfo({
    //   ...pageInfo,
    //   totalPages: optionmenuList?.PagingDetails?.TotalPages,
    //   isPagerChange: false,
    // });
    let sortOrder = sorting.orderBy;
    if (accessor === sorting.column) {
      sortOrder = sorting.orderBy === "asc" ? "desc" : "asc";
    }
    setSorting({ column: accessor, orderBy: sortOrder });
  };

  /**
   * This function calls the "getAllGroup" store funtion that get configuration list from API
   */
  const callGetOptionMenuListService = useCallback(() => {
    // const searchQuery = freeWordSearch.trim();
    // if (searchQuery || showInvalidStores) {
    //   GetSearchOptionRegistrationFilterService(
    //     pageInfo.currentPage,
    //     pageLimitInfo.pageLimit,
    //     searchQuery,
    //     showInvalidStores
    //   );
    // } else {
    GetSearchOptionRegistrationFilterService(
      pageInfo.currentPage,
      pageLimitInfo.pageLimit,
      freeWordSearch,
      showInvalidStores,
      sorting.column,
      sorting.orderBy
    );
    // }
  }, [
    freeWordSearch,
    showInvalidStores,
    GetSearchOptionRegistrationFilterService,
    pageInfo.currentPage,
    pageLimitInfo.pageLimit,
    sorting.orderBy,
    sorting.column,
  ]);

  useEffect(() => {
    GetAllBusinessTypeService();
    GetAllTimeService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    callGetOptionMenuListService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callGetOptionMenuListService]);

  useEffect(() => {
    if (updateStoreOptionOrderState.success) {
      setSorting((prevState) => {
        return { column: "sod_order_id", orderBy: prevState.orderBy };
      });
      callGetOptionMenuListService();
      resetUpdateStoreOptionOrderState();
    }
  }, [
    callGetOptionMenuListService,
    resetUpdateStoreOptionOrderState,
    updateStoreOptionOrderState.success,
  ]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "language" of the useEffect changed.
   */
  useEffect(() => {
    if (islanguageChanged) {
      callGetOptionMenuListService();
      updateLanguageChangedFlag();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [islanguageChanged]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "GroupList" of the useEffect changed.
   */
  useEffect(() => {
    setPageInfo({
      ...pageInfo,
      totalPages: optionmenuList?.PagingDetails?.TotalPages,
      isPagerChange: false,
      isRefreshPage: false,
    });
    setDisablePagination(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allOptionMenu]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "pageInfo.currentPage" of the useEffect changed.
   */
  useEffect(() => {
    if (pageInfo.isPagerChange) {
      callGetOptionMenuListService();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageInfo.currentPage]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateGroupstate.success" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateOptionMenuState.success) {
      resetAddUpdateOptionMenuState();
      callGetOptionMenuListService();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addUpdateOptionMenuState.success]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "deleteStoreManagementSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (deleteOptionMenuState.success) {
      resetDeleteOptionMenu();
      if (allOptionMenu?.length == 1) {
        if (pageInfo.currentPage > 1)
          setPageInfo({
            ...pageInfo,
            currentPage: 1,
            isRefreshPage: true,
            isPagerChange: true,
          });
        else callGetOptionMenuListService();
      } else callGetOptionMenuListService();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteOptionMenuState.success]);

  /**
   * This function shows the add user pop up from where we can add new user and update existing user. we use id to identify
   * that we are adding or updating user.
   * "Id == -1" : we are adding new user
   * "Id != -1" : means we are updating existing user
   */
  const openAddOptionMenuHandler = (row: { Id: number }) => {
    setSelectedId(row.Id);
    props.addToggleHandler();
  };

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "pageLimitInfo.pageLimit" of the useEffect changed.
   */
  useEffect(() => {
    if (pageLimitInfo.isPageLimitChange) {
      callGetOptionMenuListService();
      setPageLimit({ ...pageLimitInfo, isPageLimitChange: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLimitInfo.pageLimit]);

  useEffect(() => {
    if (searchInput === "" && !showInvalidStores) {
      resetOptionStoreFilterDetail();
      setFreeWordSearch("");
      setShowInvalidStores(false);
    }
  }, [resetOptionStoreFilterDetail, searchInput, showInvalidStores]);

  /**
   * This function deletes the user by providing Id to the Api
   */
  const deleteHandler = () => {
    DeleteOptionMenuService(selectedId);
  };

  const columns = getColumns<any, number>(
    [
      {
        columnType: ColumnType.Hidden,
        accessorKey: "Id",
      },
      {
        columnType: ColumnType.Sorting,
        accessorKey: "OrderId",
        displayName: "order_id",
        sortingName: "sod_order_id",
      },
      {
        columnType: ColumnType.JsxElement,
        jsxElementFormatter: (info: CellContext<any, any>) => {
          const row = info.row.original;
          return (
            <>
              <p>{row.OptionName}</p>
            </>
          );
        },
        accessorKey: "OptionName",
        displayName: "option_name",
        sortingName: "option_name",
      },

      {
        columnType: ColumnType.TextHeader,
        accessorKey: "RegistrationType",
        displayName: "registration_type",
      },
      {
        columnType: ColumnType.JsxElement,
        jsxElementFormatter: (info: CellContext<any, any>) => {
          const row = info.row.original;
          return (
            <>
              <p><i className="bi bi-currency-yen me-1"></i> {formatNumberWithCommas(row.FeeIncludingTax)}</p>
            </>
          );
        },
        accessorKey: "FeeIncludingTax",
        displayName: "fee",
        sortingName: "fee_including_tax",
      },
      {
        columnType: ColumnType.JsxElement,
        jsxElementFormatter: (info: CellContext<any, any>) => {
          const row = info.row.original;
          return (
            <>
              <p> {row.TimeValue}</p>
            </>
          );
        },
        accessorKey: "Time",
        displayName: "time",
        sortingName: "time_value",
      },

      {
        columnType: ColumnType.JsxElement,
        jsxElementFormatter: (info: CellContext<any, any>) => {
          const row = info.row.original;
          return (
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                openAddOptionMenuHandler(row);
              }}
            >
              <i className="bi bi-eye me-2"></i>
              <FormattedMessage id="see_details" />
            </Button>
          );
        },
        accessorKey: "isEdit",
        displayName: "",
      },
    ],
    sorting,
    handleSortingChange
  );

  const handleDragEnd = (result: {
    source: { index: number; id: string; item: any };
    destination: { index: number; id: string; item: any };
  }) => {
    UpdateStoreOptionOrderIdService([
      result.source.item,
      result.destination.item,
    ]);
  };

  return (
    <React.Fragment>
      <FormLoader
        loading={
          inProgress ||
          addUpdateOptionMenuState.inProgress ||
          deleteOptionMenuState.inProgress
        }
      />

      {props.showAddPopup && (
        <AddEditOptionMenu
          id={selectedId}
          modalClosed={props.addToggleHandler}
        />
      )}
      {props.showDeletePopup && (
        <DeletePopup
          title="delete_User"
          modalSubmit={deleteHandler}
          modalClosed={props.deletePopupToggleHandler}
          message="message_delete_user_confirm"
          isLoading={deleteOptionMenuState.inProgress}
        />
      )}
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-6">
            <div className="page_title_wrap">
              <h1 className="page_title">
                <FormattedMessage id="option_management" />
              </h1>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <FormattedMessage id="master_management" />
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  <FormattedMessage id="option_management" />
                </li>
              </ol>
            </div>
          </div>
          <div className="col-6">
            <ul className="add-list">
              <li>
                <LanguageSlider />
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="container-fluid mt-4 p-0">
        <div className="row align-items-center mb-3">
          <div className="col-lg-8 col-6 search_box">
            <div className="row">
              <div className="col-xl-4 col-sm-8 col-12">
                <div className="input-group">
                  <div className="input-group-text">
                    <FontAwesomeIcon
                      icon={faSearch}
                      onClick={handleSearchClick}
                    />
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={formatMessage("search_by_free_word")}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleSearchClick();
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-4 col-6">
            <div className="d-flex justify-content-end align-items-center">
              <input
                type="checkbox"
                className="form-check-input m-0"
                checked={showInvalidStores}
                onChange={() => handleInvalidTreatmentFilter()}
              />
              <label className="form-check-label ms-1">
                {formatMessage("show_invalid_options")}
              </label>

              {getOptionStoreFilter.IsFilterActive && (
                <button
                  className={`ms-3 btn ${
                    getOptionStoreFilter.IsFilterActive
                      ? "btn-primary"
                      : "btn-outline-secondary"
                  }`}
                  onClick={() => {
                    resetOptionStoreFilterDetail();
                    setFreeWordSearch("");
                    setSearchInput("");
                    setShowInvalidStores(false);
                    setPageInfo({...pageInfo, currentPage: 1, isRefreshPage: true});
                  }}
                  title={formatMessage(
                    getOptionStoreFilter.IsFilterActive
                      ? "clear_filter"
                      : "apply_filter"
                  )}
                >
                  <svg
                    id="fi_3126668"
                    enableBackground="new 0 0 512 512"
                    height="18"
                    viewBox="0 0 512 512"
                    width="18"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="#ffffff"
                  >
                    <g>
                      <path d="m215.546 85.327h-162.264c-18.073 0-28.679 20.379-18.31 35.187.133.199-3.448-4.682 130.024 177.006 5.921 8.587 4.149-.599 4.149 190.987 0 19.245 21.993 30.358 37.542 18.791 57.536-43.372 71.516-48.257 71.516-70.955 0-133.909-1.721-130.311 4.149-138.823l27.851-37.923c-70.082-25.496-112.087-99.608-94.657-174.27z"></path>
                      <path d="m281.951 30.166c-75.076 67.31-38.685 187.35 55.962 206.05 75.479 15.948 143.193-43.867 143.193-116.945 0-102.594-122.364-157.159-199.155-89.105zm118.529 106.804c9.515 9.466 2.715 25.676-10.603 25.676-8.014 0-10.022-3.79-28.462-22.158-18.064 17.984-20.27 22.158-28.472 22.158-13.349 0-20.063-16.264-10.603-25.676l17.769-17.699-17.769-17.699c-14.107-14.035 7.142-35.322 21.216-21.297l17.859 17.779 17.849-17.779c14.074-14.025 35.331 7.254 21.216 21.297l-17.769 17.699z"></path>
                    </g>
                  </svg>
                </button>
              )}
              {isOperationPermittedRoleBase(
                ModuleType.OptionsManagement,
                ActionType.Add
              ) && (
                <button
                  className="btn btn-primary ms-3"
                  onClick={() => {
                    openAddOptionMenuHandler({ Id: -1 });
                  }}
                >
                  <i className="bi bi-plus-lg me-1"></i>
                  {formatMessage("add_option")}
                </button>
              )}
            </div>
          </div>
        </div>
        {allOptionMenu.length > 0 ? (
          <div className={`${openPanel ? "panelOpen" : ""}`}>
            <div>
              <div className="card changingPanel">
                <div className="tableDesign row-eq-height tableDevices">
                  <div className="table-responsive" data-testid="table">
                    {allOptionMenu && (
                      <Grid
                        data={allOptionMenu}
                        columns={columns}
                        isInProgress={false}
                        hiddenColumns={["Id"]}
                        draggable={true}
                        onDragEnd={handleDragEnd}
                        getRowId={(row: any) => {
                          return row.Id.toString();
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
              {allOptionMenu.length > 0 ? (
                <div className="d-flex align-items-center justify-content-center p-2 tableFooter">
                  <Pagination
                    isRefreshPage={pageInfo.isRefreshPage}
                    totalPages={pageInfo.totalPages}
                    pageLimit={pageLimitInfo.pageLimit}
                    onPageChanged={onPageChanged}
                    disablePagination={disablePagination}
                    setIsRefreshPage={setIsRefreshPage}
                  />

                  <select
                    data-testid="pageSelect"
                    className="pageLimit ms-1"
                    onChange={onPageLimitChanged}
                    value={pageLimitInfo.pageLimit}
                  >
                    {pageLimitOptions.map((op) => (
                      <option key={op} value={op}>
                        {op} {formatMessage("rows")}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                ""
              )}
            </div>
          </div>
        ) : (
          <div className="message text-center">
            <img src={noMessageIcon} alt="" className="no_message" />
            {freeWordSearch.trim().length > 0 || showInvalidStores ? (
              <>
                <p className="noData text-muted text-center h4 mb-0">
                  <FormattedMessage id="no_data_available" />
                </p>
              </>
            ) : (
              <>
                <p className="noData text-muted text-center h4 mb-0">
                  <FormattedMessage id="no_option_menu" />
                </p>
                <p className="mb-3">
                  {formatMessage("no_option_menu_message")}
                </p>
                {isOperationPermittedRoleBase(
                  ModuleType.OptionsManagement,
                  ActionType.Add
                ) && (
                  <LoaderButton
                    text="add_option"
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                      openAddOptionMenuHandler({ Id: -1 })
                    }
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default AddPopupHOC(
  ShowPopupHOC(DeletePopupHOC(observer(StoreOptionMenuList)))
);
